import { css } from 'lit';
/* eslint-disable max-lines */
import {
	ChangeSet,
	ChangeSetItem,
	EventStatus,
	LaneBlockage,
	LaneImpactValue,
	RespondingUnitMember,
	Timeline,
	TimelineEntry,
	WorkZoneRequestStatus,
} from '@typings/api';
import { TimelineDay, TimelineDayEntry, TimelineDayEntryChange } from '@typings/shared-types';
import { LOCALE } from '@/constants';
import { ConfigREMEventForm } from '@config/ConfigREM';
import { isLaneImpacted, isValidNumber } from '@/utils/utils';
import { FrameNewLineRegex } from '@/constants';
import { FrameLineJustificationRegex } from '@/constants';
import { FramePageJustificationRegex } from '@/constants';
import { TemplateResult, html } from 'lit';
import { numberIsValidAndFinite } from './utils';
import formatTimezoneSup from './format-timezone-sup';

interface FieldGroup {
	name: string;
	changeSetName: string;
	fields: string[];
	formatSummary: (items: ChangeSetItem[], timeStamp: number) => string | null;
	renderer?: (items: ChangeSetItem[]) => TemplateResult;
	delimiter?: string;
	highlighted?: boolean;
}

const formatDate = (date: number | Date) =>
	new Date(date).toLocaleString(LOCALE, ConfigREMEventForm.timelineDateFormatOptions);

const formatDateAndTime = (date: number | Date) =>
	new Date(date).toLocaleString(LOCALE, ConfigREMEventForm.wholeDatestampFormatOptions);

const laneBlockageToTimelineEntry = (
	laneBlockage?: LaneBlockage,
	oldLaneBlockage?: LaneBlockage,
): string => {
	if (isLaneImpacted(laneBlockage) && laneBlockage) {
		const lanesAffected: number[] = [...(laneBlockage.lanesAffected ?? [])].sort();
		const numLanes = lanesAffected.length;
		const impactMessages = [];
		if (numLanes > 0) {
			impactMessages.push(
				`Lane${numLanes > 1 ? 's' : ''} ${
					numLanes > 1
						? `${lanesAffected.slice(0, numLanes - 1).join(', ')} and ${
								lanesAffected[numLanes - 1]
						  }`
						: lanesAffected[0]
				}`,
			);
		}
		if (laneBlockage.insideShoulderAffected) {
			impactMessages.push('inside shoulder');
		}
		if (laneBlockage.outsideShoulderAffected) {
			impactMessages.push('outside shoulder');
		}
		if (laneBlockage.entranceRampAffected) {
			impactMessages.push('entrance ramp');
		}
		if (laneBlockage.exitRampAffected) {
			impactMessages.push('exit ramp');
		}
		if (laneBlockage.allLanesAffected && numLanes === 0) {
			impactMessages.push('Affected direction added');
		}
		if (oldLaneBlockage?.allLanesAffected && laneBlockage?.allLanesAffected === false) {
			impactMessages.push('Affected direction removed');
		}
		return impactMessages.join(', ');
	}
	if (oldLaneBlockage?.allLanesAffected && laneBlockage?.allLanesAffected === false) {
		return 'Affected direction removed';
	}
	return 'No lanes impacted';
};

const formatSignMessageChange = (item: ChangeSetItem) => {
	const messageOverrideAsStrings = (item.value as string)
		.replace(FrameNewLineRegex, '\n\t')
		.replace(FramePageJustificationRegex, '')
		.split(FrameLineJustificationRegex);

	const signName = messageOverrideAsStrings.shift()?.replace(':', '');
	const messageText = messageOverrideAsStrings.pop();

	return `${signName}:\n\t${messageText}`;
};

const fieldNameMappings: Record<string, string> = {
	routeDesignator: 'Route',
	mileStart: 'Start MP',
	mileEnd: 'End MP',
	county: 'County',
	district: 'District',
	subdistrict: 'Sub-district',
	unit: 'Unit',
	region: 'Region',
	city: 'City',
	crossStreetStart: 'Start Cross Street',
	crossStreetEnd: 'End Cross Street',
	proximity: 'Proximity',
	internalContactDetails: 'Internal contact details',
	additionalPublicText: 'Additional public text',
	additionalPublicAudio: 'Additional public audio',
	eastboundLaneBlockage: 'Eastbound',
	westboundLaneBlockage: 'Westbound',
	northboundLaneBlockage: 'Northbound',
	southboundLaneBlockage: 'Southbound',
	licensePlate: 'License plate',
	description: 'Description',
	respondingUnitType: 'Responding unit type',
	respondingUnitMember: 'Responding unit member',
	recordStatus: 'Status',
	assigned: 'Assigned',
	arrived: 'Arrived',
	completed: 'Completed',
};

const fieldGroups: FieldGroup[] = [
	{
		name: 'Publish Status',
		changeSetName: 'event',
		fields: ['publishStatus'],
		formatSummary: () => '',
		renderer: (items: ChangeSetItem[]) => {
			const publishStatusKey = items[0].value as keyof typeof WorkZoneRequestStatus;

			return html`<work-zone-status-badge
				style="display: flex; height: 30px; align-items: center;"
				.publishStatus=${WorkZoneRequestStatus[publishStatusKey]}
			></work-zone-status-badge>`;
		},
	},
	{
		name: 'Rejection Reason',
		changeSetName: 'event',
		fields: ['publishNotes'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items[0].value as string;
		},
		highlighted: true,
	},
	{
		name: 'Scheduling',
		changeSetName: 'event',
		fields: ['eventStart', 'eventEnd'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items
				.map((item) => {
					const time: number = numberIsValidAndFinite(item.value) ? item.value : NaN;

					let subject = '';

					if (item.fieldName === 'eventStart') {
						subject = time < Date.now() ? 'Event started' : 'Scheduled start';
					} else if (item.fieldName === 'eventEnd') {
						subject = 'Scheduled end';
					}
					const description = !Number.isNaN(time) ? formatDateAndTime(time) : 'removed';

					return `${subject} - ${description}`;
				})
				.join('\n');
		},
	},
	{
		name: 'Event Status',
		changeSetName: 'event',
		fields: ['eventStatus'],
		formatSummary: (items: ChangeSetItem[], timeStamp: number) => {
			const previousEventStatus = items[0].previousValue as EventStatus;
			const eventStatus = items[0].value as EventStatus;
			let subject = null;

			if (eventStatus === EventStatus.COMPLETED) {
				subject = 'Event closed';
			} else if (
				eventStatus === EventStatus.NORMAL &&
				previousEventStatus === EventStatus.COMPLETED
			) {
				subject = 'Event reopened';
			}

			if (subject == null) {
				return null;
			}

			return `${subject}: ${formatDateAndTime(timeStamp)}`;
		},
	},
	{
		name: 'Headline',
		changeSetName: 'event',
		fields: ['eventType'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map((item) => item.value).join(', ');
		},
	},
	{
		name: 'Source',
		changeSetName: 'event',
		fields: ['eventSource', 'reporterName', 'reporterPhoneNumber'],
		formatSummary: (items: ChangeSetItem[]) => {
			const changes = items.map((item) => item.value);
			return `${changes.join(', ')}`;
		},
	},
	{
		name: 'Display on 511',
		changeSetName: 'event',
		fields: ['includeInEventFeed'],
		formatSummary: (items: ChangeSetItem[]) => {
			const displayOn511 = items[0].value as boolean;

			return displayOn511 ? 'Event sent to 511' : 'Event excluded from 511';
		},
	},
	{
		name: 'Location',
		changeSetName: 'event',
		fields: [
			'routeDesignator',
			'mileStart',
			'mileEnd',
			'county',
			'unit',
			'subdistrict',
			'region',
			'city',
			'crossStreetStart',
			'crossStreetEnd',
			'proximity',
		],
		formatSummary: (items: ChangeSetItem[]) => {
			const sortedItems = items.sort((itemA, itemB) => {
				const keys = Object.keys(fieldNameMappings);

				return keys.indexOf(itemA.fieldName ?? '') - keys.indexOf(itemB.fieldName ?? '');
			});

			const changes = sortedItems.map((item) => {
				let value = item.value as string;

				if (['mileStart', 'mileEnd'].includes(item.fieldName)) {
					value = (item.value as number)?.toFixed(1);
				}
				return `${fieldNameMappings[item.fieldName]}: ${value ?? 'unknown/removed'}`;
			});

			return `${changes.join('\n')}`;
		},
	},
	{
		name: 'Lane impacts',
		changeSetName: 'event',
		fields: [
			'eastboundLaneBlockageType',
			'westboundLaneBlockageType',
			'northboundLaneBlockageType',
			'southboundLaneBlockageType',
			'eastboundLaneBlockage',
			'westboundLaneBlockage',
			'northboundLaneBlockage',
			'southboundLaneBlockage',
		],
		formatSummary: (items: ChangeSetItem[]) => {
			const directionMap = new Map<string, { type?: ChangeSetItem; blockage?: ChangeSetItem }>();

			items.forEach((item) => {
				if (!item.fieldName) return;

				const match = item.fieldName.match(/^(eastbound|westbound|northbound|southbound)/);
				if (!match) return;

				const direction = match[1];

				if (!directionMap.has(direction)) {
					directionMap.set(direction, {});
				}

				const entry = directionMap.get(direction);

				if (item.fieldName.includes('Type')) {
					entry.type = item;
				} else if (item.fieldName.includes('Blockage')) {
					entry.blockage = item;
				}
			});

			const summaries: string[] = [];

			directionMap.forEach((pair, _) => {
				summaries.push(
					`${fieldNameMappings[pair.blockage?.fieldName as string]} - ${laneBlockageToTimelineEntry(
						pair.blockage?.value as LaneBlockage,
						pair.blockage?.previousValue as LaneBlockage,
					)} ${
						pair.type?.value !== null &&
						(pair.type?.value as LaneImpactValue) !== LaneImpactValue.NA
							? `(${pair.type?.value as string})`
							: ''
					}`,
				);
			});

			return summaries.join('\n ');
		},
	},
	{
		name: 'Location Notes',
		changeSetName: 'event',
		fields: ['locationDescription'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map((item) => item.value).join('\n');
		},
		highlighted: true,
	},
	{
		name: 'Notes',
		changeSetName: 'event',
		fields: ['internalContactDetails', 'additionalPublicText', 'additionalPublicAudio'],
		formatSummary: (items: ChangeSetItem[]) => {
			const changes = items.map(
				(item) => `${fieldNameMappings[item.fieldName]}: ${item.value as string}`,
			);

			return `${changes.join('\n')}`;
		},
		highlighted: true,
	},
	{
		name: 'Visual Verification',
		changeSetName: 'event',
		fields: ['verified'],
		formatSummary: (items: ChangeSetItem[]) => {
			const item = items[0];

			return `${
				isValidNumber(item.value) ? `Verified on ${formatDateAndTime(item.value)}` : `Unverified`
			} by user "${item.createdBy as string}"`;
		},
	},
	{
		name: 'Displayed on Sign',
		changeSetName: 'signs-display',
		fields: ['Added'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map(formatSignMessageChange).join('\n');
		},
	},
	{
		name: 'Removed from Sign',
		changeSetName: 'signs-display',
		fields: ['Removed'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map(formatSignMessageChange).join('\n');
		},
	},
	{
		name: 'Sign Update',
		changeSetName: 'signs',
		fields: ['Added', 'Removed'],
		formatSummary: (items: ChangeSetItem[]) => {
			const addedSigns: string[] = [];
			const removedSigns: string[] = [];

			items.forEach((item) => {
				const itemAsString = `\t${item.value as string}`;

				if (item.fieldName === 'Added') {
					addedSigns.push(itemAsString);
				} else if (item.fieldName === 'Removed') {
					removedSigns.push(itemAsString);
				}
			});

			const summaryParts: string[] = [];

			if (addedSigns.length > 0) {
				summaryParts.push(`Message Added to Queues: \n${addedSigns.join('\n')}`);
			}
			if (removedSigns.length > 0) {
				summaryParts.push(`Message Removed from Queues: \n${removedSigns.join('\n')}`);
			}
			return summaryParts.join('\n');
		},
		delimiter: '\n',
	},
	{
		name: 'Sign Message Edited',
		changeSetName: 'messages',
		fields: ['Added', 'Removed'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map(formatSignMessageChange).join('\n');
		},
		highlighted: true,
	},
	{
		name: 'Vehicles',
		changeSetName: 'vehicles',
		fields: ['licensePlate', 'description'],
		formatSummary: (items: ChangeSetItem[]) => {
			const summaryParts: string[] = [];

			items.forEach((item) => {
				if (item.fieldName === 'licensePlate') {
					summaryParts.push(
						`${fieldNameMappings[item.fieldName]}: ${(item.value as string).replace(
							ConfigREMEventForm.vehicles.licensePlateNumberDelimitter,
							' - ',
						)}`,
					);
				}
				if (item.fieldName === 'description') {
					summaryParts.push(`${fieldNameMappings[item.fieldName]}: ${item.value as string}`);
				}
			});

			return summaryParts.join('\n');
		},
	},
	{
		name: 'Responding Units',
		changeSetName: 'respondingUnits',
		fields: [
			'arrived',
			'respondingUnitMember',
			'respondingUnitType',
			'assigned',
			'completed',
			'recordStatus',
		],
		formatSummary: (items: ChangeSetItem[]) => {
			return items
				.sort((itemA, itemB) => {
					const keys = Object.keys(fieldNameMappings);

					return keys.indexOf(itemA.fieldName ?? '') - keys.indexOf(itemB.fieldName ?? '');
				})
				.map((item) => {
					const fieldName = item.fieldName as string;
					const label = `${fieldNameMappings[fieldName]}`;
					let value = '';
					if (
						['arrived', 'assigned', 'completed'].includes(fieldName) &&
						numberIsValidAndFinite(item.value)
					) {
						value = formatDateAndTime(item.value);
					} else if (fieldName === 'respondingUnitType' || fieldName === 'recordStatus') {
						value = item.value as string;
					} else if (fieldName === 'respondingUnitMember') {
						value = (item.value as RespondingUnitMember)?.name?.toString() ?? 'removed';
					}

					return `${label}: ${value}`;
				})
				.join('\n');
		},
	},
	{
		name: 'Dispositions',
		changeSetName: 'respondingUnitDispositions',
		fields: ['dispositionType'],
		formatSummary: (items: ChangeSetItem[]) => {
			const changes = items.map((item) => `${item.value as string}`);

			return `${changes.join('\n')}`;
		},
		delimiter: ', ',
	},
	{
		name: 'Attributes',
		changeSetName: 'attributes',
		fields: ['value'],
		formatSummary: (items: ChangeSetItem[]) => {
			const addedAttributes: string[] = [];
			const removedAttributes: string[] = [];
			const summaryParts: string[] = [];

			items.forEach((item) => {
				if (item.value == null && item.previousValue != null) {
					removedAttributes.push(item.previousValue as string);
				} else if (item.value != null) {
					addedAttributes.push(item.value as string);
				}
			});

			if (addedAttributes.length > 0) {
				summaryParts.push(`Added: ${addedAttributes.join(', ')}`);
			}
			if (removedAttributes.length > 0) {
				summaryParts.push(`Removed: ${removedAttributes.join(', ')}`);
			}

			return summaryParts.join('\n');
		},
	},
	{
		name: 'Priority',
		changeSetName: 'event',
		fields: ['priority'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items[0].value as string;
		},
	},
	{
		name: 'Linked events',
		changeSetName: 'eventLinkages',
		fields: ['linkedEventB'],
		formatSummary: (items: ChangeSetItem[]) => {
			const linkedEventIds: number[] = [];

			items.forEach((item) => {
				if (numberIsValidAndFinite(item.value)) {
					linkedEventIds.push(item.value);
				}
			});

			return linkedEventIds.map((id) => `#${id}`).join(', ');
		},
		delimiter: ', ',
	},
	{
		name: 'Email Notification Message',
		changeSetName: 'event',
		fields: ['text'],
		formatSummary: (items: ChangeSetItem[]) => {
			return `${(items[0].value ?? 'N/A') as string}`;
		},
	},
	{
		name: 'Email Notification Recipients',
		changeSetName: 'emailRecipients',
		fields: ['emailAddress', 'Added', 'Removed'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map((item) => `${item.fieldName as string}: ${item.value as string}`).join('\n');
		},
	},
	{
		name: 'Email Notification Groups',
		changeSetName: 'emailGroups',
		fields: ['Added', 'Removed'],
		formatSummary: (items: ChangeSetItem[]) => {
			return items.map((item) => `${item.fieldName as string}: ${item.value as string}`).join('\n');
		},
	},
];

interface GroupedChanges {
	groupName: string;
	items: ChangeSetItem[];
	summary: string;
	renderer?: TemplateResult;
}

function groupChangeSetItems(
	changeSetName: string,
	timeStamp: number,
	changeSetItems: ChangeSetItem[] = [],
	groups: FieldGroup[] = fieldGroups,
): GroupedChanges[] {
	const grouped: GroupedChanges[] = [];

	const groupedItemIndices = new Set<number>();

	groups.forEach((group) => {
		const matchingItems: ChangeSetItem[] = [];

		if (changeSetName !== group.changeSetName) {
			return;
		}

		changeSetItems.forEach((item, index) => {
			if (item.fieldName && group.fields.includes(item.fieldName)) {
				matchingItems.push(item);
				groupedItemIndices.add(index);
			}
		});

		if (matchingItems.length > 0) {
			const existingGroup = grouped.find(
				(groupedChanges) => groupedChanges.groupName === group.name,
			);
			if (existingGroup != null) {
				existingGroup.items = [...existingGroup.items, ...matchingItems];
				existingGroup.summary = [
					existingGroup.summary,
					group.formatSummary(matchingItems, timeStamp),
				].join('\n');

				if (group.renderer != null) {
					existingGroup.renderer = [existingGroup.renderer, group.renderer(matchingItems)];
				}
			} else {
				const summary = group.formatSummary(matchingItems, timeStamp);

				if (summary == null) {
					return;
				}

				grouped.push({
					groupName: group.name,
					items: matchingItems,
					summary,
					renderer: group.renderer ? group.renderer(matchingItems) : undefined,
				});
			}
		}
	});

	return grouped;
}

function getUserFromChangeSet(changeSet: ChangeSet): string {
	const userItem = changeSet.changeSetItems?.find((item) => item.createdBy);
	if (userItem?.createdBy) {
		return userItem.createdBy;
	}

	return changeSet.name || 'Guest User';
}

export function formatChangeSet(
	changeSet: ChangeSet,
	timeStamp: number,
): {
	changeSetInfo: {
		name?: string;
		id?: number;
		revisionNumber?: number;
	};
	groupedChanges: GroupedChanges[];
} {
	return {
		changeSetInfo: {
			name: changeSet.name,
			id: changeSet.id,
			revisionNumber: changeSet.revisionNumber,
		},
		groupedChanges: groupChangeSetItems(changeSet.name ?? '', timeStamp, changeSet.changeSetItems),
	};
}

// AI-generated helper function
function groupChangeSetsByTimestamp(
	entries: TimelineEntry[],
	thresholdMs: number = 1000,
): TimelineEntry[] {
	if (entries.length === 0) return [];

	const result: TimelineEntry[] = [];
	const changeSetMap = new Map<string, ChangeSetItem[]>();
	let groupTimestamp = entries[0].timestamp;

	entries.forEach((item, i) => {
		const timeDiff = i > 0 ? Math.abs(item.timestamp - entries[i - 1].timestamp) : 0;

		if (timeDiff > thresholdMs) {
			const changeSets = Array.from(changeSetMap.entries()).map(([name, items]) => ({
				name,
				changeSetItems: items,
			}));
			result.push({
				timestamp: groupTimestamp,
				changeSets,
			});

			changeSetMap.clear();
			groupTimestamp = item.timestamp;
		}

		item.changeSets?.forEach((changeSet) => {
			const existing = changeSetMap.get(changeSet.name) || [];
			changeSetMap.set(changeSet.name, [...existing, ...changeSet.changeSetItems]);
		});

		if (i === entries.length - 1) {
			const changeSets = Array.from(changeSetMap.entries()).map(([name, items]) => ({
				name,
				changeSetItems: items,
			}));
			result.push({
				timestamp: groupTimestamp,
				changeSets,
			});
		}
	});

	return result;
}

export function processTimelineToTimelineDays(
	timeline?: Timeline,
	groups: FieldGroup[] = fieldGroups,
): TimelineDay[] {
	if (timeline == null) {
		return [];
	}

	const dayMap = new Map<string, TimelineDayEntry[]>();
	const entriesGroupedByTime = groupChangeSetsByTimestamp(timeline.entries ?? []);

	entriesGroupedByTime.forEach((entry) => {
		if (!entry.timestamp) return;

		const date = new Date(entry.timestamp);
		const dateString = formatDate(date);
		const timeString = formatTimezoneSup(
			date.toLocaleTimeString(LOCALE, ConfigREMEventForm.timelineTimeFormatOptions),
		);

		entry.changeSets?.forEach((changeSet) => {
			const summariesByGroup = new Map<string, string[]>();
			const renderersByGroup = new Map<string, TemplateResult[]>();
			const timeStamp = date.getTime();

			const groupedChanges = groupChangeSetItems(
				changeSet.name ?? '',
				timeStamp,
				changeSet.changeSetItems ?? [],
				groups,
			);

			groupedChanges.forEach((group) => {
				if (!summariesByGroup.has(group.groupName)) {
					summariesByGroup.set(group.groupName, []);
				}
				summariesByGroup.get(group.groupName)!.push(group.summary);

				if (!renderersByGroup.has(group.groupName)) {
					renderersByGroup.set(group.groupName, []);
				}
				if (group.renderer) {
					renderersByGroup.get(group.groupName)!.push(group.renderer);
				}
			});

			const allGroupedChanges: TimelineDayEntryChange[] = [];
			summariesByGroup.forEach((summaries, groupName) => {
				const group = groups.find((g) => g.name === groupName);
				const delimiter = group?.delimiter ?? '\n---\n';
				const highlighted = group?.highlighted ?? false;
				const renderer = renderersByGroup.get(groupName);

				allGroupedChanges.push({
					subject: groupName,
					description: summaries.join(delimiter),
					renderer,
					highlighted,
				});
			});

			if (allGroupedChanges.length === 0) return;

			const user = getUserFromChangeSet(changeSet);
			const dayEntry: TimelineDayEntry = {
				timeString,
				user,
				changes: allGroupedChanges.sort((a, b) => {
					const indexA = groups.findIndex((group) => group.name === a.subject);
					const indexB = groups.findIndex((group) => group.name === b.subject);
					return indexA - indexB;
				}),
			};

			if (!dayMap.has(dateString)) {
				dayMap.set(dateString, []);
			}
			dayMap.get(dateString)!.push(dayEntry);
		});
	});

	const timelineDays: TimelineDay[] = Array.from(dayMap.entries())
		.map(([dateString, entries]) => ({
			dateString,
			entries,
		}))
		.sort((a, b) => {
			const dateA = new Date(a.dateString);
			const dateB = new Date(b.dateString);
			return dateB.getTime() - dateA.getTime();
		});

	return timelineDays;
}

export function processSignUpdatesToTimelineDays(
	timeline?: Timeline,
	groups: FieldGroup[] = fieldGroups,
): TimelineDay[] {
	const timelineDays = processTimelineToTimelineDays(timeline, groups);
	const signUpdateGroupNames = [
		'Displayed on Sign',
		'Removed from Sign',
		'Sign Update',
		'Sign Message Edited',
	];

	return timelineDays
		.map((day) => ({
			...day,
			entries: day.entries
				.map((entry) => ({
					...entry,
					changes: entry.changes.filter((change) => signUpdateGroupNames.includes(change.subject)),
				}))
				.filter((entry) => entry.changes.length > 0),
		}))
		.filter((day) => day.entries.length > 0);
}
